import * as _ from 'lodash-es';

type ChannelMessage = {
  type: 'fetch';
  input: RequestInit;
  output: {
    status: number;
    statusText: string;
    url: string;
    responseText: string;
  };
};

type ChannelInputPayload<T extends ChannelMessage> = {
  id: string;
  clientId: string;
  type: T['type'];
  input: T['input'];
};

type ChannelOutputPayload<T extends ChannelMessage> = {
  id: string;
  clientId: string;
  output: T['output'];
};

const clientId = Math.random().toString(36).substring(2, 15);

const pendingMessages = new Map<
  string,
  {
    resolve: (value: unknown) => void;
  }
>();

enum EventNames {
  ChannelSend = 'slab-extension.ui.channel.send',
  ChannelReply = 'slab-extension.ui.channel.reply',
}

export const sendMessageToExtension = <
  TYPE extends ChannelMessage['type'],
  T extends Extract<ChannelMessage, { type: TYPE }>,
>(
  type: TYPE,
  input: T['input'],
): Promise<T['output']> => {
  const { promise, resolve } = Promise.withResolvers<T['output']>();

  const detail: ChannelInputPayload<T> = {
    id: _.uniqueId('content-script-message-'),
    clientId,
    type: type as T['type'],
    input,
  };

  pendingMessages.set(detail.id, {
    resolve: resolve as (value: unknown) => void,
  });

  document.dispatchEvent(
    new CustomEvent(EventNames.ChannelSend, {
      detail,
    }),
  );

  return promise;
};

// eslint-disable-next-line import/no-unused-modules -- used by extension
export const onMessageFromExtension = <
  TYPE extends ChannelMessage['type'],
  T extends Extract<ChannelMessage, { type: TYPE }>,
>(
  type: TYPE,
  handler: (data: T['input']) => Promise<T['output'] | undefined>,
) => {
  document.addEventListener(EventNames.ChannelSend, (event) => {
    const detail = (event as CustomEvent).detail as ChannelInputPayload<T>;

    if (detail.clientId === clientId || detail.type !== type) return;

    handler(detail.input).then((response) => {
      if (!response) return;
      const outputPayload: ChannelOutputPayload<ChannelMessage> = {
        id: detail.id,
        clientId,
        output: response,
      };
      document.dispatchEvent(
        new CustomEvent(EventNames.ChannelReply, {
          detail: outputPayload,
        }),
      );
    });
  });
};

document.addEventListener(EventNames.ChannelReply, (event) => {
  const detail = (event as CustomEvent)
    .detail as ChannelOutputPayload<ChannelMessage>;

  pendingMessages.get(detail.id)?.resolve(detail.output);
});
